/* eslint-disable react/no-danger */
import { IPageContent } from "api/interfaces/static-content.type";
import Head from "next/head";
import BreadcrumbsMetaHead from "../../component/meta-header/BreadcrumbsMetaHead";
import { getThumbImagePath } from "../../util/helper/getThumbImagePath";
import { capitalize } from "../../util/lodash/capitalize";

interface IProps{
  staticContent: IPageContent;
}

function ManufacturerMetaHead(props: IProps) {
  const {
    query, totalHits, products,
    tier3, tier2, tier1, tier0,
    make, model, year, bannerData, staticContent, manufacturer,
  } = props;
  const { pathname } = query;
  let { keywords, description, title } = staticContent.metaData || {};
  const { bannerImageUrl = "" } = bannerData || staticContent.pageDetails;
  let { pageNumber = 1 } = query;
  const totalPages = Math.ceil(totalHits / 12);

  if (make || model || year) {
    description = `Find ${capitalize(manufacturer)} ${capitalize(tier3 || tier2 || tier1 || tier0)} to fit your ${capitalize(year)} ${capitalize(make)} ${capitalize(model)} here. Get All ${capitalize(year)} ${capitalize(model)} auto parts at PartsAvatar.ca`;
    title = `${capitalize(manufacturer)} ${capitalize(year)} ${capitalize(make)} ${capitalize(model)} ${capitalize(
      tier3 || tier2 || tier1 || tier0,
    )}`;
  } else {
    description = `Find ${capitalize(manufacturer)} ${capitalize(tier3 || tier2 || tier1 || tier0)} to fit your vehicle here. Get All auto parts at PartsAvatar.ca`;
    title = `PartsAvatar.ca: ${capitalize(manufacturer)} ${capitalize(
      tier3 || tier2 || tier1 || tier0,
    )}`;
  }

  if (!keywords) {
    keywords = make ? `${manufacturer} ${year} ${make} ${model} ${tier3 || tier2 || tier1 || tier0}` : `${manufacturer} ${tier3 || tier2 || tier1 || tier0}`;
  } else {
    keywords = keywords.join(", ");
  }

  title = title.trim();
  const meta = {
    title,
    image: bannerImageUrl,
    description,
    meta: {
      robots: "all",
    },
    keywords,
  };

  const itemListElement = products.map((product: any, position: any) => {
    const { canonicalUrl, description: pDesc, thumbnail } = product;
    return {
      "@type": "ListItem",
      position,
      url: `https://partsavatar.ca${canonicalUrl}`,
      name: pDesc,
      description: pDesc,
      image: getThumbImagePath(thumbnail),
    };
  });
  const microdata = {
    "@context": "http://schema.org/",
    "@type": "CollectionPage",
    mainEntity: {
      "@type": "ItemList",
      itemListElement,
      numberOfItems: totalHits,
    },
  };

  pageNumber = parseInt(pageNumber, 10);
  const url = `https://partsavatar.ca/${pathname}`;
  const canonicalUrl = pageNumber > 1 ? `https://partsavatar.ca/${pathname}?pageNumber=${pageNumber}` : `https://partsavatar.ca/${pathname}`;
  const frCanonicalUrl = pageNumber > 1 ? `https://partsavatar.ca/fr/${pathname}?pageNumber=${pageNumber}` : `https://partsavatar.ca/fr/${pathname}`;
  return (
    <>

      <Head>

        <title>{meta.title}</title>

        <meta name="description" content={meta.description} />

        <meta name="keywords" content={meta.keywords} />

        <meta name="robots" content={meta.meta.robots} />

        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={canonicalUrl} />
        <link rel="alternate" hrefLang="fr" href={frCanonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        {pageNumber > 2 && pageNumber <= totalPages && (
          <link
            rel="prev"
            href={`https://partsavatar.ca/${pathname}?pageNumber=${pageNumber - 1}`}
          />
        )}
        {pageNumber === 2 && pageNumber <= totalPages && (
          <link
            rel="prev"
            href={`https://partsavatar.ca/${pathname}`}
          />
        )}
        {pageNumber < totalPages && (
          <link
            rel="next"
            href={`https://partsavatar.ca/${pathname}?pageNumber=${pageNumber + 1}`}
          />
        )}
        {" "}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(microdata) }}
        />

        <meta property="og:title" content={meta.title} />

        <meta property="og:description" content={meta.description} />

        <meta property="og:type" content="product.group" />

        <meta property="og:url" content={url} />

        <meta property="og:site_name" content="PartsAvatar.ca" />

        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:title" content={meta.title} />

        <meta name="twitter:description" content={meta.description} />

        <meta name="twitter:image" content={meta.image} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "WebPage",
              primaryImageOfPage: {
                "@type": "ImageObject",
                url: bannerImageUrl,
              },
            }),
          }}
        />
      </Head>

      <BreadcrumbsMetaHead />
    </>
  );
}

export default ManufacturerMetaHead;
